import { useState } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { cn } from "../lib/utils"
import { IMAGE_SIZE, OBJECT_FIT } from "../lib/style-resolver"

export function ImageRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  const [imageFailed, setImageFailed] = useState(false)
  const atomDefaults = useAtomDefaults("image")
  const resolvedVariant = (element as { variant?: string }).variant
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"
  const roundedValue = (element as unknown as { rounded?: boolean | string }).rounded
  const roundedClass = (() => {
    if (roundedValue === true || roundedValue === "true") return "rounded-md"
    if (!roundedValue || roundedValue === "false") return ""
    return roundedValue.startsWith("rounded-") ? roundedValue : `rounded-${roundedValue}`
  })()
  const src = String(resolveSymbol(element.src ?? "") ?? "")
  const alt = String(resolveSymbol(element.alt ?? "") ?? "")

  const sizeClasses = element.size ? IMAGE_SIZE[element.size] ?? "" : ""
  const hasExplicitSize = !!sizeClasses

  // Resolve styling props
  const styling = element.styling as Record<string, unknown> | undefined
  const opts = element.options as Record<string, unknown> | undefined
  const flatImageProperties = element as unknown as {
    fit?: unknown
    width?: unknown
    height?: unknown
  }
  const effectiveWidth = styling?.width ?? flatImageProperties.width
  const effectiveHeight = styling?.height ?? flatImageProperties.height
  const objectFit = normalizeObjectFit(styling?.objectFit ?? flatImageProperties.fit)
  const aspectRatioRaw = styling?.aspectRatio ?? element.aspectRatio
  const frameStyle: React.CSSProperties = {}
  const width = normalizeDimension(effectiveWidth)
  const height = normalizeDimension(effectiveHeight)
  if (width) frameStyle.width = width
  if (height) frameStyle.height = height
  if (aspectRatioRaw) {
    const ar = String(aspectRatioRaw)
    frameStyle.aspectRatio = ar === "square" ? "1 / 1" : ar.replace(":", "/")
  }
  const imageStyle: React.CSSProperties = objectFit ? { objectFit } : {}
  const nativeImageStyle: React.CSSProperties = { ...frameStyle, ...imageStyle }
  const hasFrameStyle = Object.keys(frameStyle).length > 0
  const hasImageStyle = Object.keys(imageStyle).length > 0
  const hasNativeImageStyle = Object.keys(nativeImageStyle).length > 0
  const dataAttrs = {
    "data-atom-type": "image",
    "data-variant": resolvedVariant,
    "data-size": resolvedSize,
    "data-rounded": roundedValue === undefined
      ? undefined
      : roundedClass ? "true" : "false",
    "data-fit": objectFit,
    "data-width": effectiveWidth == null ? undefined : String(effectiveWidth),
    "data-height": effectiveHeight == null ? undefined : String(effectiveHeight),
  }

  // Detect sizing/aspect classes in className that require a wrapper div.
  // Without a wrapper, <img> ignores Tailwind size constraints and renders at
  // its intrinsic (original) dimensions, causing layout overflow.
  const cls = element.className ?? ""
  const hasSizingClass = /\b(size-|aspect-|w-\d|h-\d|max-w-|max-h-)/.test(cls)
  const unwrapNativeImage = opts?.unwrap === true
  const needsWrapper = !unwrapNativeImage && (
    hasExplicitSize || effectiveWidth != null || effectiveHeight != null ||
    aspectRatioRaw || roundedClass || hasSizingClass
  )

  if (!needsWrapper) {
    if (!src) return null
    if (imageFailed) {
      return (
        <div
          role="img"
          aria-label={alt || "Image unavailable"}
          className={cn(
            "flex min-h-24 items-center justify-center rounded-md border border-dashed border-border bg-muted/40 px-3 text-center text-xs text-muted-foreground",
            element.className,
          )}
          style={hasNativeImageStyle ? nativeImageStyle : undefined}
          {...dataAttrs}
        >
          {alt || "Image unavailable"}
        </div>
      )
    }
    return (
      <img
        src={src}
        alt={alt}
        onError={() => setImageFailed(true)}
        className={cn(
          objectFit ? OBJECT_FIT[objectFit] ?? "" : undefined,
          element.className,
        )}
        style={hasNativeImageStyle ? nativeImageStyle : undefined}
        {...dataAttrs}
      />
    )
  }

  return (
    <div
      {...dataAttrs}
      className={cn(
        "overflow-hidden",
        sizeClasses,
        !hasExplicitSize && (aspectRatioRaw === "1:1" || aspectRatioRaw === "square") && "aspect-square",
        (element.fit || objectFit) && "bg-muted",
        roundedClass,
        element.className,
      )}
      style={hasFrameStyle ? frameStyle : undefined}
    >
      {src && !imageFailed ? (
        <img
          src={src}
          alt={alt}
          onError={() => setImageFailed(true)}
          className={cn(
            "h-full w-full",
            objectFit ? OBJECT_FIT[objectFit] ?? "object-cover" : undefined,
          )}
          style={hasImageStyle ? imageStyle : undefined}
        />
      ) : (
        <div
          role="img"
          aria-label={alt || "Image unavailable"}
          className="flex h-full w-full flex-col items-center justify-center gap-2 bg-muted/50 px-3 text-center text-xs text-muted-foreground"
        >
          <svg className="h-8 w-8 text-muted-foreground/30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z" />
          </svg>
          <span>{alt || "Image unavailable"}</span>
        </div>
      )}
    </div>
  )
}

function normalizeDimension(value: unknown): string | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return `${value}px`
  if (typeof value !== "string") return undefined
  const trimmed = value.trim()
  if (!trimmed) return undefined
  return /^-?\d+(?:\.\d+)?$/.test(trimmed) ? `${trimmed}px` : trimmed
}

function normalizeObjectFit(value: unknown): React.CSSProperties["objectFit"] | undefined {
  return value === "cover" || value === "contain" || value === "fill" || value === "none" || value === "scale-down"
    ? value
    : undefined
}
